import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";

/** Connecteurs de ping : marche à suivre et scripts prêts à coller, par marque, avec l'URL réelle de la sonde. */

const Bloc = ({ titre, contenu, testid }) => {
  const [copie, setCopie] = useState(false);
  const copier = async () => {
    await navigator.clipboard.writeText(contenu);
    setCopie(true);
    toast.success("Copié");
    setTimeout(() => setCopie(false), 2500);
  };
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900 overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-white/10">
        <span className="text-[11px] uppercase tracking-[0.18em] text-white/50">{titre}</span>
        <button
          data-testid={`${testid}-copy`}
          onClick={copier}
          className="text-[11px] font-semibold text-[#4D93FF] inline-flex items-center gap-1.5 hover:text-white"
        >
          {copie ? <Check size={12} /> : <Copy size={12} />} Copier
        </button>
      </div>
      <pre className="px-3 py-3 text-[11px] leading-relaxed text-white/85 whitespace-pre-wrap break-all font-mono-bk">{contenu}</pre>
    </div>
  );
};

const Etapes = ({ etapes }) => (
  <ol className="space-y-1.5 text-sm text-slate-700 list-decimal list-inside">
    {etapes.map((e, i) => (
      <li key={i}>{e}</li>
    ))}
  </ol>
);

const scriptSentinelle = (url, cible) => `#!/bin/sh
# BKPROD — signe de vie « je vais bien » (à poser sur un appareil allumé en permanence du site)
# Vérifie que l'équipement répond, puis prévient BKPROD. Si l'équipement est HS ou le site coupé,
# l'appel ne part pas et BKPROD ouvre un ticket automatiquement.
URL="${url}"
CIBLE="${cible}"   # IP de l'équipement à surveiller (NVR, contrôleur, borne)

if ping -c 2 -W 3 "$CIBLE" > /dev/null 2>&1; then
  curl -fsS -m 10 "$URL" > /dev/null 2>&1 || wget -q -O /dev/null -T 10 "$URL"
fi`;

const cronSentinelle = (intervalle) => `# Installation (une seule fois, en root)
# 1) coller le script dans /usr/local/bin/bkpro-ping.sh
chmod +x /usr/local/bin/bkpro-ping.sh
# 2) déclencher toutes les ${intervalle} minutes
( crontab -l 2>/dev/null; echo "*/${intervalle} * * * * /usr/local/bin/bkpro-ping.sh" ) | crontab -
# 3) vérifier
/usr/local/bin/bkpro-ping.sh && echo "ping envoyé"`;

const powershell = (url, cible, intervalle) => `# BKPROD — signe de vie depuis un poste Windows du site
$url   = "${url}"
$cible = "${cible}"   # IP de l'équipement à surveiller

if (Test-Connection -ComputerName $cible -Count 2 -Quiet) {
  try { Invoke-WebRequest -Uri $url -TimeoutSec 10 -UseBasicParsing | Out-Null } catch {}
}

# Enregistrer ce fichier dans C:\\BKPRO\\ping.ps1 puis créer la tâche planifiée (invite de commandes admin) :
# schtasks /create /tn "BKPRO Ping" /tr "powershell -NoProfile -ExecutionPolicy Bypass -File C:\\BKPRO\\ping.ps1" /sc minute /mo ${intervalle} /ru SYSTEM`;

const MARQUES = {
  loxone: {
    label: "Loxone (Miniserver)",
    intro:
      "Le Miniserver appelle l'URL tout seul : aucun matériel ni script à ajouter. C'est le connecteur le plus fiable, il tombe en même temps que le courant ou Internet du site.",
    etapes: (u, n) => [
      "Ouvrir Loxone Config et se connecter au Miniserver.",
      "Périphériques > Réseau > clic droit > « Ajouter une sortie virtuelle » (Virtual Output).",
      "Adresse / URL du serveur : coller la partie « base » ci-dessous.",
      "Sur cette sortie virtuelle, ajouter une « Commande de sortie virtuelle » : dans « Commande pour ON », coller le chemin ci-dessous, méthode GET.",
      `Dans le programme, relier cette sortie à un bloc « Générateur d'impulsions » réglé sur ${n} minutes (ou une minuterie astronomique/périodique).`,
      "Enregistrer dans le Miniserver, puis cliquer sur « Envoyer un signe de vie (test) » ici pour vérifier que l'appareil passe en ligne.",
    ],
    blocs: (u) => {
      const [base, chemin] = [u.slice(0, u.indexOf("/api/")), u.slice(u.indexOf("/api/"))];
      return [
        { titre: "Adresse du serveur (sortie virtuelle)", contenu: base || u },
        { titre: "Commande pour ON (chemin)", contenu: chemin || u },
        { titre: "URL complète (pour test navigateur)", contenu: u },
      ];
    },
  },
  unv: {
    label: "NVR UNV / Uniview",
    intro:
      "Les NVR UNV n'appellent pas d'URL et n'ont pas de shell utilisable. On utilise donc un appareil « sentinelle » déjà présent sur le site (Raspberry Pi, mini-PC, NAS Synology, box Linux) qui vérifie que le NVR répond, puis prévient BKPROD.",
    etapes: (u, n, c) => [
      "Repérer l'IP fixe du NVR sur le site (Réglages > Réseau > TCP/IP).",
      "Sur un appareil du site allumé en permanence, créer le fichier /usr/local/bin/bkpro-ping.sh avec le script ci-dessous.",
      `Remplacer CIBLE par l'IP du NVR (${c}), puis installer la tâche cron toutes les ${n} minutes.`,
      "Lancer le script une fois à la main : l'appareil doit passer « En ligne » dans cette page.",
      "Si aucun appareil Linux n'est disponible sur le site, utiliser l'onglet Windows (tâche planifiée sur le poste de supervision).",
    ],
    blocs: (u, n, c) => [
      { titre: "Script sentinelle (Linux / Raspberry / NAS)", contenu: scriptSentinelle(u, c) },
      { titre: "Installation cron", contenu: cronSentinelle(n) },
    ],
  },
  reyee: {
    label: "Reyee / Ruijie",
    intro:
      "Les bornes et switches Reyee ne permettent pas de programmer un appel HTTP sortant. On surveille donc la passerelle Reyee (ou la borne principale) depuis un appareil du site, avec le même script sentinelle.",
    etapes: (u, n, c) => [
      "Noter l'IP de la passerelle ou de la borne Reyee principale (visible dans Ruijie Cloud ou l'EWEB).",
      "Poser le script ci-dessous sur un appareil du site allumé en permanence (NAS, mini-PC, Raspberry).",
      `Remplacer CIBLE par l'IP Reyee (${c}) puis installer la tâche cron toutes les ${n} minutes.`,
      "Astuce : surveiller la passerelle plutôt qu'une borne isolée, cela détecte aussi les coupures Internet du site.",
    ],
    blocs: (u, n, c) => [
      { titre: "Script sentinelle (Linux / Raspberry / NAS)", contenu: scriptSentinelle(u, c) },
      { titre: "Installation cron", contenu: cronSentinelle(n) },
    ],
  },
  unifi: {
    label: "UniFi (UDM / Cloud Key)",
    intro:
      "Une UDM, UDM Pro ou Cloud Key tourne sous Linux : le ping part directement de la passerelle, sans matériel supplémentaire. Attention : une mise à jour majeure UniFi OS peut effacer le cron, à re-vérifier après upgrade.",
    etapes: (u, n) => [
      "Se connecter en SSH à la passerelle (utilisateur root, SSH activé dans les réglages UniFi OS).",
      "Créer /usr/local/bin/bkpro-ping.sh avec le script ci-dessous (il n'a pas besoin de cible : la passerelle se surveille elle-même).",
      `Installer le cron toutes les ${n} minutes.`,
      "Vérifier que l'appareil passe « En ligne » dans cette page.",
    ],
    blocs: (u, n) => [
      {
        titre: "Script (la passerelle se signale elle-même)",
        contenu: `#!/bin/sh
# BKPROD — signe de vie de la passerelle UniFi
curl -fsS -m 10 "${u}" > /dev/null 2>&1 || wget -q -O /dev/null -T 10 "${u}"`,
      },
      { titre: "Installation cron", contenu: cronSentinelle(n) },
    ],
  },
  windows: {
    label: "Poste Windows",
    intro:
      "Quand le site dispose d'un PC de supervision, d'une caisse ou d'un serveur allumé en permanence, une tâche planifiée suffit. Le script vérifie d'abord l'équipement à surveiller.",
    etapes: (u, n, c) => [
      "Enregistrer le script ci-dessous dans C:\\BKPRO\\ping.ps1.",
      `Remplacer $cible par l'IP de l'équipement à surveiller (${c}).`,
      "Ouvrir une invite de commandes en administrateur et coller la ligne schtasks (dernière ligne du script).",
      "Lancer la tâche une fois pour vérifier que l'appareil passe « En ligne » ici.",
    ],
    blocs: (u, n, c) => [{ titre: "PowerShell + tâche planifiée", contenu: powershell(u, c, n) }],
  },
  navigateur: {
    label: "Test manuel",
    intro:
      "Pour vérifier une URL sans rien installer : ouvrez-la dans un navigateur ou lancez la commande ci-dessous. L'appareil doit passer « En ligne » immédiatement dans cette page.",
    etapes: (u) => [
      "Coller l'URL dans un navigateur : une réponse { ok: true } confirme que la sonde est reconnue.",
      "Ou utiliser la commande curl depuis n'importe quel poste.",
      "Attention : un test manuel ne remplace pas une tâche planifiée, sans appel régulier l'appareil repassera hors ligne.",
    ],
    blocs: (u) => [
      { titre: "URL de vérification", contenu: u },
      { titre: "Commande curl", contenu: `curl -i "${u}"` },
    ],
  },
};

const ORDRE = ["loxone", "unv", "reyee", "unifi", "windows", "navigateur"];

export const ConnecteurSonde = ({ url, intervalle = 10, marque = "loxone" }) => {
  const [onglet, setOnglet] = useState(ORDRE.includes(marque) ? marque : "unv");
  const [cible, setCible] = useState("192.168.1.50");
  const conf = MARQUES[onglet];
  const blocs = conf.blocs(url, intervalle, cible);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-4" data-testid="connecteur-sonde">
      <div className="flex flex-wrap gap-2">
        {ORDRE.map((k) => (
          <button
            key={k}
            data-testid={`connecteur-onglet-${k}`}
            onClick={() => setOnglet(k)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              onglet === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-500"
            }`}
          >
            {MARQUES[k].label}
          </button>
        ))}
      </div>

      <p className="text-sm text-slate-600" data-testid="connecteur-intro">
        {conf.intro}
      </p>

      {["unv", "reyee", "windows"].includes(onglet) && (
        <label className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
          IP de l'équipement à surveiller
          <input
            data-testid="connecteur-cible-input"
            value={cible}
            onChange={(e) => setCible(e.target.value)}
            className="h-9 w-40 rounded-lg border border-slate-200 px-2 font-mono-bk text-xs"
          />
          <span className="text-slate-400">(le script n'envoie le signe de vie que si cette IP répond)</span>
        </label>
      )}

      <Etapes etapes={conf.etapes(url, intervalle, cible)} />

      <div className="space-y-2">
        {blocs.map((b, i) => (
          <Bloc key={b.titre} titre={b.titre} contenu={b.contenu} testid={`connecteur-bloc-${onglet}-${i}`} />
        ))}
      </div>
    </div>
  );
};

export default ConnecteurSonde;
